import DataWithLabel from '@lyra/core/components/DataWithLabel'
import Section from '@lyra/core/components/Section'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatTruncatedNumber from '@lyra/core/utils/formatTruncatedNumber'
import formatTruncatedUSD from '@lyra/core/utils/formatTruncatedUSD'
import { InstrumentType } from '@lyra/web/constants/instruments'
import { TRADE_HEADER_MARKET_SELECT_WIDTH } from '@lyra/web/constants/layout'
import { MarketId } from '@lyra/web/constants/markets'
import { CurrencyId } from '@lyra/web/constants/tokens'
import useCurrencies from '@lyra/web/hooks/useCurrencies'
import useMarketStats from '@lyra/web/hooks/useMarketStats'
import useTicker from '@lyra/web/hooks/useTicker'
import { getPerpInstrumentName } from '@lyra/web/utils/instruments'
import { coerce } from '@lyra/web/utils/types'
import { StackProps, XStack } from 'tamagui'

import MarketsDropdown from './MarketsDropdown'

type Props = {
  currencyId: CurrencyId
  instrumentType: InstrumentType
} & StackProps

const PerpFundingData = ({ instrumentName }: { instrumentName: string }) => {
  const ticker = useTicker(instrumentName)
  const fundingRate = ticker ? parseFloat(ticker.perp_details.funding_rate) : 0
  const annualFundingRate = fundingRate * 24 * 365
  return (
    <DataWithLabel
      label="1Y Funding"
      value={
        ticker
          ? formatPercentage(annualFundingRate, {
              showSign: true,
              dps: 3,
            })
          : '...'
      }
      color={fundingRate >= 0 ? 'green' : 'red'}
      isSmall
    />
  )
}

export default function MarketHeaderSections({ currencyId, instrumentType, ...stackProps }: Props) {
  const currencies = useCurrencies()
  const { data: stats, isLoading } = useMarketStats(currencyId, instrumentType)

  const marketId = coerce(MarketId, currencyId)

  const { spotPrice, spotPrice24hPctChange } = currencies[currencyId]

  const dailyVolume = stats ? stats.daily_notional_volume : 0
  const openInterest = stats ? +stats.open_interest * spotPrice : 0
  const dailyTrades = stats ? stats.daily_trades : 0

  return (
    <XStack {...stackProps} overflow="hidden" $mobile={{ flexDirection: 'column' }}>
      <MarketsDropdown
        noTopBorder
        $desktop={{ width: TRADE_HEADER_MARKET_SELECT_WIDTH }}
        $mobile={{ width: '100%' }}
        selectedInstrumentType={instrumentType}
        selectedCurrencyId={currencyId}
      />
      <Section
        noTopBorder
        $desktop={{ flexGrow: 1, flexShrink: 1 }}
        $mobile={{ width: '100%' }}
        borderLeftWidth={1}
      >
        <Section.Grid minColWidth={80} maxColWidth={90}>
          <DataWithLabel
            label={`${marketId ? marketId : currencyId} Price`}
            value={`$${formatNumber(spotPrice, { minDps: 2, maxDps: 18 })}`}
            isSmall
          />
          <DataWithLabel
            label="24h Change"
            color={spotPrice24hPctChange >= 0 ? 'green' : 'red'}
            value={formatPercentage(spotPrice24hPctChange, { showSign: true })}
            isSmall
          />
          {instrumentType === InstrumentType.Perps && marketId ? (
            <PerpFundingData instrumentName={getPerpInstrumentName(marketId)} />
          ) : null}
          <DataWithLabel
            label="24h Volume"
            value={isLoading ? '...' : formatTruncatedUSD(+dailyVolume)}
            isSmall
          />
          {instrumentType !== InstrumentType.Spot ? (
            <DataWithLabel
              label="Open Interest"
              value={isLoading ? '...' : formatTruncatedUSD(openInterest)}
              isSmall
            />
          ) : null}
          <DataWithLabel
            label="24h Trades"
            value={isLoading ? '...' : formatTruncatedNumber(dailyTrades)}
            isSmall
          />
        </Section.Grid>
      </Section>
    </XStack>
  )
}
